import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FileText,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  User,
  Calendar,
  DollarSign,
  ChevronDown,
  RefreshCw,
  MapPin,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import AssignProviderModal from '@/components/modals/AssignProviderModal';
import PauseJobSessionModal from '@/components/modals/PauseJobSessionModal';
import EscalateJobModal from '@/components/modals/EscalateJobModal';
import CompletionApprovalModal from '@/components/modals/CompletionApprovalModal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Booking {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  service_name: string;
  category_name?: string;
  total_amount: number;
  status: 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
  booking_status?: string;
  payment_status?: string;
  created_at: string;
  updated_at: string;
  provider_id?: number | null;
  provider_name?: string;
  booking_date?: string;
  booking_time_start?: string;
  booking_time_end?: string;
  postcode?: string;
  customer_location?: string;
  property_type?: string;
  quote_id?: number | null;
  service_notes?: string;
  internal_notes?: string;
  customer_notes?: string;
  provider_accepted_at?: string;
  job_started_at?: string;
  job_completed_at?: string;
  payment_completed_at?: string;
  invoice_id?: string;
}

const AdminBookingsPage: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignProviderModal, setShowAssignProviderModal] = useState(false);
  const [showPauseJobModal, setShowPauseJobModal] = useState(false);
  const [selectedBookingForPause, setSelectedBookingForPause] = useState<Booking | null>(null);
  const [showEscalateJobModal, setShowEscalateJobModal] = useState(false);
  const [selectedBookingForEscalate, setSelectedBookingForEscalate] = useState<Booking | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedBookingForCompletion, setSelectedBookingForCompletion] = useState<Booking | null>(null);
  
  // Form states
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load bookings
  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/admin/bookings`);
      if (response.data) {
        setBookings(response.data);
      } else if (response.success) {
        // Handle case where success is true but data is on different property
        setBookings(response as unknown as Booking[]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setMessage({ type: 'error', text: 'Failed to load bookings' });
      setLoading(false);
    }
  };

  // ============================================================
  // BOOKING HANDLERS
  // ============================================================

  const handleAssignProvider = async (providerId: string) => {
    if (!selectedBooking) {
      setMessage({ type: 'error', text: 'Please select a booking' });
      return;
    }
    if (!providerId) {
      setMessage({ type: 'error', text: 'Please select a provider' });
      return;
    }

    try {
      const response = await apiService.request(`/admin/bookings/${selectedBooking.id}/assign-provider`, 'PUT', {
        providerId: providerId,
        assignedBy: user?.id || 'admin',
        assignedAt: new Date().toISOString()
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Provider assigned successfully' });
        setShowAssignProviderModal(false);
        setSelectedBooking(null);
        loadBookings();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to assign provider' });
      }
    } catch (error: any) {
      console.error('Error assigning provider:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to assign provider' });
    }
  };

  const handlePauseJob = async (pauseReason: string, resumeTime: string) => {
    if (!selectedBookingForPause) {
      setMessage({ type: 'error', text: 'Please select a booking' });
      return;
    }
    if (!pauseReason) {
      setMessage({ type: 'error', text: 'Please provide a reason for pause' });
      return;
    }

    try {
      const response = await apiService.request(`/admin/bookings/${selectedBookingForPause.id}/pause-job`, 'PUT', {
        pauseReason: pauseReason,
        resumeTime: resumeTime,
        pausedBy: user?.id || 'admin',
        pausedAt: new Date().toISOString(),
        status: 'paused'
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Job paused successfully' });
        setShowPauseJobModal(false);
        setSelectedBookingForPause(null);
        loadBookings();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to pause job' });
      }
    } catch (error: any) {
      console.error('Error pausing job:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to pause job' });
    }
  };

  const handleEscalateJob = async (escalationReason: string, priority: string) => {
    if (!selectedBookingForEscalate) {
      setMessage({ type: 'error', text: 'Please select a booking' });
      return;
    }
    if (!escalationReason) {
      setMessage({ type: 'error', text: 'Please provide escalation reason' });
      return;
    }

    try {
      const response = await apiService.request(`/admin/bookings/${selectedBookingForEscalate.id}/escalate`, 'POST', {
        escalationReason: escalationReason,
        priority: priority,
        escalatedBy: user?.id || 'admin',
        escalatedAt: new Date().toISOString(),
        notifyProvider: true,
        notifyCustomer: true
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Job escalated successfully' });
        setShowEscalateJobModal(false);
        setSelectedBookingForEscalate(null);
        loadBookings();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to escalate job' });
      }
    } catch (error: any) {
      console.error('Error escalating job:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to escalate job' });
    }
  };

  const handleApproveCompletion = async (completionNotes: string, qualityRating: number) => {
    if (!selectedBookingForCompletion) {
      setMessage({ type: 'error', text: 'Please select a booking' });
      return;
    }
    if (!completionNotes) {
      setMessage({ type: 'error', text: 'Please provide completion notes' });
      return;
    }

    try {
      const response = await apiService.request(`/admin/bookings/${selectedBookingForCompletion.id}/approve-completion`, 'PUT', {
        completionNotes: completionNotes,
        qualityRating: qualityRating,
        approvedBy: user?.id || 'admin',
        approvedAt: new Date().toISOString(),
        status: 'completed',
        remarketed: false
      });

      if (response.success) {
        setMessage({ type: 'success', text: 'Job completion approved' });
        setShowCompletionModal(false);
        setSelectedBookingForCompletion(null);
        loadBookings();
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to approve completion' });
      }
    } catch (error: any) {
      console.error('Error approving completion:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to approve completion' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'confirmed':
      case 'assigned':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'in_progress':
        return <AlertCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredBookings = bookings.filter(booking =>
    (booking.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
    (booking.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
    (booking.service_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
  );

  if (!user || user.role !== 'admin') {
    return (
      <Layout>
        <div className="section-padding bg-background">
          <div className="container-custom">
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">Only admins can access this page.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Admin - Bookings Management | VireWire</title>
        <meta name="description" content="Manage customer bookings and track progress" />
      </Helmet>

      <section className="section-padding bg-background">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="admin-h1 text-primary mb-2 flex items-center gap-2">
                <FileText className="h-8 w-8" />
                Bookings Management
              </h1>
              <p className="admin-small text-muted-foreground">Track and manage customer bookings</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadBookings()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              <span>{message.text}</span>
              <button onClick={() => setMessage(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex gap-4 flex-col md:flex-row">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by customer name, email, or service..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bookings Cards */}
          <div className="space-y-4">
            {loading ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  Loading bookings...
                </CardContent>
              </Card>
            ) : filteredBookings.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  No bookings found matching your search.
                </CardContent>
              </Card>
            ) : (
              filteredBookings.map((booking) => (
                <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {/* Header with Status */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold">{booking.customer_name}</h3>
                          <p className="text-sm text-muted-foreground">{booking.customer_email}</p>
                        </div>
                        <Badge className={getStatusColor(booking.status || 'pending')}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(booking.status || 'pending')}
                            {(booking.status || 'pending').charAt(0).toUpperCase() + (booking.status || 'pending').slice(1)}
                          </span>
                        </Badge>
                      </div>

                      {/* Grid with Complete Details - Matching Quote Card Format */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {/* ADDRESS */}
                      <div className="border-l-2 border-primary/20 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">ADDRESS</p>
                        <p className="font-medium text-sm">{booking.customer_location || 'N/A'}</p>
                      </div>

                      {/* POSTCODE */}
                      <div className="border-l-2 border-primary/20 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">POSTCODE</p>
                        <p className="font-medium">{booking.postcode || 'N/A'}</p>
                      </div>

                      {/* CATEGORY */}
                      <div className="border-l-2 border-primary/20 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">CATEGORY</p>
                        <p className="font-medium text-sm">{booking.category_name || 'N/A'}</p>
                      </div>

                      {/* PHONE */}
                      <div className="border-l-2 border-primary/20 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">PHONE</p>
                        <p className="font-medium">{booking.customer_phone || 'N/A'}</p>
                      </div>

                      {/* PROPERTY */}
                      <div className="border-l-2 border-green-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">PROPERTY</p>
                        <p className="font-medium text-sm">{booking.property_type || 'N/A'}</p>
                      </div>

                      {/* SERVICE TIME */}
                      <div className="border-l-2 border-green-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SERVICE TIME</p>
                        <p className="font-medium">{booking.booking_time_start || 'N/A'}</p>
                      </div>

                      {/* SCHEDULE TIME */}
                      <div className="border-l-2 border-green-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SCHEDULE TIME</p>
                        <p className="font-medium">{booking.booking_time_start || 'N/A'}</p>
                      </div>

                      {/* SCHEDULE DATE */}
                      <div className="border-l-2 border-green-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SCHEDULE DATE</p>
                        <p className="font-medium text-sm">
                          {booking.booking_date 
                            ? new Date(booking.booking_date).toLocaleDateString('en-GB')
                            : 'N/A'
                          }
                        </p>
                      </div>

                      {/* SERVICE DATE */}
                      <div className="border-l-2 border-blue-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SERVICE DATE</p>
                        <p className="font-medium text-sm">
                          {booking.booking_date 
                            ? new Date(booking.booking_date).toLocaleDateString('en-GB')
                            : 'N/A'
                          }
                        </p>
                      </div>

                      {/* SUBMITTED TIME */}
                      <div className="border-l-2 border-blue-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SUBMITTED TIME</p>
                        <p className="font-medium">
                          {new Date(booking.created_at).toLocaleTimeString('en-GB', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: false 
                          })}
                        </p>
                      </div>

                      {/* SUBMITTED DATE */}
                      <div className="border-l-2 border-blue-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SUBMITTED DATE</p>
                        <p className="font-medium text-sm">
                          {new Date(booking.created_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>

                      {/* SERVICE INFO - Added for reference */}
                      <div className="border-l-2 border-purple-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">SERVICE</p>
                        <p className="font-medium text-sm">{booking.service_name}</p>
                      </div>

                      {/* PROVIDER */}
                      <div className="border-l-2 border-purple-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">PROVIDER</p>
                        <p className="font-medium text-sm">{booking.provider_name || 'Not Assigned'}</p>
                      </div>

                      {/* AMOUNT */}
                      <div className="border-l-2 border-orange-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">AMOUNT</p>
                        <p className="font-bold text-lg">£{booking.total_amount ? (booking.total_amount / 100).toFixed(2) : '0.00'}</p>
                      </div>

                      {/* Updated Date */}
                      <div className="border-l-2 border-blue-200 pl-3">
                        <p className="text-muted-foreground text-xs font-medium">🔄 Updated</p>
                        <p className="font-medium text-sm">
                          {new Date(booking.updated_at).toLocaleDateString('en-GB')}
                        </p>
                      </div>
                    </div>

                    {/* Notes Section */}
                    {(booking.service_notes || booking.internal_notes || booking.customer_notes) && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        {booking.service_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Service Notes</p>
                            <p className="text-sm bg-gray-50 p-2 rounded">{booking.service_notes}</p>
                          </div>
                        )}
                        {booking.customer_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Customer Notes</p>
                            <p className="text-sm bg-gray-50 p-2 rounded">{booking.customer_notes}</p>
                          </div>
                        )}
                        {booking.internal_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Internal Notes</p>
                            <p className="text-sm bg-gray-50 p-2 rounded">{booking.internal_notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowDetailsModal(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                      {!booking.provider_name && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowAssignProviderModal(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Assign Provider
                        </Button>
                      )}
                      {booking.status === 'in_progress' && booking.provider_name && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setSelectedBookingForPause(booking);
                            setShowPauseJobModal(true);
                          }}
                        >
                          ⏸ Pause Session
                        </Button>
                      )}
                      {booking.status === 'in_progress' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => {
                            setSelectedBookingForEscalate(booking);
                            setShowEscalateJobModal(true);
                          }}
                        >
                          ! Escalate
                        </Button>
                      )}
                      {booking.status === 'completed' && (
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setSelectedBookingForCompletion(booking);
                            setShowCompletionModal(true);
                          }}
                        >
                          ✓ Approve & Close
                        </Button>
                      )}
                    </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Details Modal */}
          <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Booking Details - #{selectedBooking?.id}</DialogTitle>
                <DialogDescription>
                  Complete booking information and status
                </DialogDescription>
              </DialogHeader>

              {selectedBooking && (
                <div className="space-y-4">
                  {/* Customer Section */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Customer Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Name</p>
                        <p className="font-medium">{selectedBooking.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Email</p>
                        <p className="font-medium">{selectedBooking.customer_email}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Phone</p>
                        <p className="font-medium">{selectedBooking.customer_phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Location</p>
                        <p className="font-medium">{selectedBooking.customer_location || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Booking Section */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Booking Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Service</p>
                        <p className="font-medium">{selectedBooking.service_name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Provider</p>
                        <p className="font-medium">{selectedBooking.provider_name || 'Not Assigned'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Quote ID</p>
                        <p className="font-medium">{selectedBooking.quote_id || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Postcode</p>
                        <p className="font-medium">{selectedBooking.postcode || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Booking Date</p>
                        <p className="font-medium">
                          {selectedBooking.booking_date 
                            ? new Date(selectedBooking.booking_date).toLocaleDateString('en-GB')
                            : 'N/A'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Start Time</p>
                        <p className="font-medium">{selectedBooking.booking_time_start || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">End Time</p>
                        <p className="font-medium">{selectedBooking.booking_time_end || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Amount</p>
                        <p className="font-bold">£{selectedBooking.total_amount ? (selectedBooking.total_amount / 100).toFixed(2) : '0.00'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Status</p>
                        <Badge className={getStatusColor(selectedBooking.status)}>
                          {selectedBooking.status}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Booking Status</p>
                        <Badge className={getStatusColor(selectedBooking.booking_status || 'pending')}>
                          {selectedBooking.booking_status || 'Pending'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Payment Status</p>
                        <Badge className={getPaymentStatusColor(selectedBooking.payment_status)}>
                          {selectedBooking.payment_status || 'Pending'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Invoice ID</p>
                        <p className="font-medium">{selectedBooking.invoice_id || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Workflow Timeline */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Workflow Timeline</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">✅ Provider Accepted</p>
                        <p className="font-medium">
                          {selectedBooking.provider_accepted_at 
                            ? new Date(selectedBooking.provider_accepted_at).toLocaleString('en-GB')
                            : 'Pending'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">🚀 Job Started</p>
                        <p className="font-medium">
                          {selectedBooking.job_started_at 
                            ? new Date(selectedBooking.job_started_at).toLocaleString('en-GB')
                            : 'Not Started'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">✔️ Job Completed</p>
                        <p className="font-medium">
                          {selectedBooking.job_completed_at 
                            ? new Date(selectedBooking.job_completed_at).toLocaleString('en-GB')
                            : 'Not Completed'
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">💰 Payment Completed</p>
                        <p className="font-medium">
                          {selectedBooking.payment_completed_at 
                            ? new Date(selectedBooking.payment_completed_at).toLocaleString('en-GB')
                            : 'Not Paid'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {(selectedBooking.service_notes || selectedBooking.internal_notes || selectedBooking.customer_notes) && (
                    <div className="bg-muted p-4 rounded-lg">
                      <h3 className="font-semibold mb-3">Notes</h3>
                      <div className="space-y-3">
                        {selectedBooking.service_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Service Notes</p>
                            <p className="text-sm">{selectedBooking.service_notes}</p>
                          </div>
                        )}
                        {selectedBooking.customer_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Customer Notes</p>
                            <p className="text-sm">{selectedBooking.customer_notes}</p>
                          </div>
                        )}
                        {selectedBooking.internal_notes && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Internal Notes</p>
                            <p className="text-sm">{selectedBooking.internal_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Timeline</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Created</p>
                        <p className="font-medium">{new Date(selectedBooking.created_at).toLocaleString('en-GB')}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs font-medium">Updated</p>
                        <p className="font-medium">{new Date(selectedBooking.updated_at).toLocaleString('en-GB')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Assign Provider Modal */}
          {selectedBooking && (
            <AssignProviderModal
              jobId={`BK-${selectedBooking.id}`}
              serviceType={selectedBooking.service_name}
              location={selectedBooking.customer_location || 'N/A'}
              scheduledTime={selectedBooking.booking_date ? `${selectedBooking.booking_date} • ${selectedBooking.booking_time_start || ''}` : 'Not Scheduled'}
              priority="High"
              isOpen={showAssignProviderModal}
              onClose={() => setShowAssignProviderModal(false)}
              onAssign={async (providerId, _notes, _notifications) => {
                await handleAssignProvider(providerId);
              }}
            />
          )}

          {/* Pause Job Session Modal */}
          {selectedBookingForPause && (
            <PauseJobSessionModal
              jobId={`BK-${selectedBookingForPause.id}`}
              providerName={selectedBookingForPause.provider_name || 'Unknown'}
              providerRole="Field Engineer"
              jobValue={`£${selectedBookingForPause.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`}
              isOpen={showPauseJobModal}
              onClose={() => {
                setShowPauseJobModal(false);
                setSelectedBookingForPause(null);
              }}
              onConfirm={async (reason, providerNotes, customerNotes, _pausePayout) => {
                // Roll the modal's extra notes into the reason string so the
                // existing handler signature (pauseReason, resumeTime) is preserved.
                const detail = [providerNotes, customerNotes].filter(Boolean).join(' | ');
                const fullReason = detail ? `${reason} — ${detail}` : reason;
                await handlePauseJob(fullReason, '');
              }}
            />
          )}

          {/* Escalate Job Modal */}
          {selectedBookingForEscalate && (
            <EscalateJobModal
              jobId={`BK-${selectedBookingForEscalate.id}`}
              currentStatus={selectedBookingForEscalate.status || 'STALLED'}
              isOpen={showEscalateJobModal}
              onClose={() => {
                setShowEscalateJobModal(false);
                setSelectedBookingForEscalate(null);
              }}
              onConfirm={async (escalationType, priorityLevel, adminAssignment, notes) => {
                // Pack modal extras into the reason string; backend handler
                // accepts (escalationReason, priority).
                const extra = [adminAssignment && `assigned to ${adminAssignment}`, notes].filter(Boolean).join(' | ');
                const reason = extra ? `${escalationType} — ${extra}` : escalationType;
                await handleEscalateJob(reason, priorityLevel || 'normal');
              }}
            />
          )}

          {/* Completion Approval Modal */}
          {selectedBookingForCompletion && (
            <CompletionApprovalModal
              jobId={`BK-${selectedBookingForCompletion.id}`}
              totalPayout={`£${selectedBookingForCompletion.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`}
              getQAProgress={() => ({
                completed: 4,
                total: 4, // 100% complete
              })}
              qaItems={[
                { name: 'Site Media', completed: true },
                { name: 'Client Sign-off', completed: true },
                { name: 'Compliance', completed: true },
                { name: 'Final Audit', completed: true },
              ]}
              isOpen={showCompletionModal}
              onClose={() => {
                setShowCompletionModal(false);
                setSelectedBookingForCompletion(null);
              }}
              onConfirm={async (releasePayload, internalNote) => {
                // handleApproveCompletion expects (completionNotes, qualityRating).
                const notes = internalNote || `Approved by admin (payload=${JSON.stringify(releasePayload)})`;
                await handleApproveCompletion(notes, 5);
              }}
            />
          )}
        </div>
      </section>
    </Layout>
  );
};

export default AdminBookingsPage;
